import { useState, useEffect } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import useAppStore from "./microsite-store";
import initializeGtm from "@util/initializeGtm";
import initializeMetaPixel from "@util/initializeMetaPixel";
import { removeExistingGTM } from "@util/initializeGtm";
import { removeExistingMetaPixel } from "@util/initializeMetaPixel";

/**
 * Custom hook for fetching microsite app data
 *
 * @example
 * // Basic usage
 * const { campaignId, entityInfo, appConfig, loading, error, fetchAppData } = useMicrositeData();
 *
 * // With remoteCodeId
 * const { campaignId, entityInfo, appConfig, loading, error, fetchAppData } = useMicrositeData("abc123", preview);
 *
 * @param {string} remoteCodeId - Remote code ID for fetching app data (optional)
 * @param {boolean} preview - Whether this is a preview request (optional)
 * @returns {Object} - Microsite app data state and methods
 */
export function useMicrositeData(remoteCodeId = null, preview = false) {
  // Use microsite store for state management
  const {
    campaignId,
    entityInfo,
    appConfig,
    setCampaignId,
    setEntityInfo,
    setAppConfig,
    setInitLoading,
    initLoading,
    setShowOtherEfforts,
    setSearchParamsOnInit,
  } = useAppStore();

  // Local state for error, modal info, and loading
  const [error, setError] = useState(null);
  const [modalInfo, setModalInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const { i18n } = useTranslation();

  // Set up axios interceptor for modal responses
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      function (response) {
        const res = response.data;
        if (res.response_code === 3) {
          setModalInfo(res.response[0]);
          return response;
        } else {
          return response;
        }
      },
      function (error) {
        return Promise.reject(error);
      }
    );

    // Cleanup interceptor on unmount
    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  /**
   * Fetch microsite app data
   * @param {string} customRemoteCodeId - Optional custom remote code ID to override the hook remoteCodeId
   * @param {boolean} customPreview - Optional custom preview flag to override the hook preview
   */
  const fetchAppData = async (
    customRemoteCodeId = null,
    customPreview = null
  ) => {
    const useRemoteCodeId = customRemoteCodeId || remoteCodeId;
    const usePreview = customPreview !== null ? customPreview : preview;

    if (!useRemoteCodeId) {
      console.error("useMicrositeData: remoteCodeId is required");
      return;
    }
    setInitLoading(true);
    setError(null);
    setLoading(true);

    try {
      const req = {
        // remote_code_id: useRemoteCodeId,
        url: useRemoteCodeId,
        campaign_url: useRemoteCodeId,
      };

      if (usePreview) req.preview = true;

      const { data } = await axios.post(`/microsite/retrieve-by-url/`, req);
      const res = data.response[0];
      setAppData(res);
    } catch (err) {
      setCampaignId(null);
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  const setAppData = async (data) => {
    setCampaignId(data.campaign_id);
    setEntityInfo({
      ...data,
      location: data.location || entityInfo.location,
    });
    setAppConfig(data.app_configuration);
    setInitLoading(false);
    setShowOtherEfforts(false);
    if (data && data.language && data.language !== "en") {
      i18n.changeLanguage(data.language);
    }

    if (data.paid_media_settings && JSON.stringify(data.paid_media_settings) !== "{}") {
      if (data.paid_media_settings.gtm_id) {
        initializeGtm(data.paid_media_settings.gtm_id);
      } else {
        removeExistingGTM();
      }
      if (data.paid_media_settings.meta_pixel_id) {
        initializeMetaPixel(data.paid_media_settings.meta_pixel_id);
      } else {
        removeExistingMetaPixel();
      }
    } else {
      removeExistingGTM();
      removeExistingMetaPixel();
    }
  };

  // Store initial search params and remove them from URL on mount
  useEffect(() => {
    // Get current store state to check if already stored
    const currentState = useAppStore.getState();

    // Only store if not already stored
    if (!currentState.searchParamsOnInit) {
      const uuidRegex =
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

      const paramsObj = {};
      // searchParams.forEach((value, key) => {
      //   if (key === "hog_id") {
      //     paramsObj[key] = value;
      //   } else if (uuidRegex.test(value)) {
      //     paramsObj[key] = value;
      //   }
      // });
      searchParams.forEach((value, key) => {
        paramsObj[key] = value;
      });

      if (Object.keys(paramsObj).length > 0) {
        setSearchParamsOnInit(paramsObj);
      }

      // Remove all search params from URL
      if (searchParams.toString()) {
        setSearchParams({}, { replace: true });
      }
    }
  }, []); // Run only once on mount

  // Auto-fetch on mount if remoteCodeId is provided
  useEffect(() => {
    if (remoteCodeId) {
      fetchAppData();
    }
  }, [remoteCodeId, preview]);

  return {
    // State from store
    campaignId,
    entityInfo,
    appConfig,
    initLoading,
    // Local state
    error,
    modalInfo,
    loading,

    // Methods
    fetchAppData,
    setAppData,

    // Setters (for manual state management if needed)
    setCampaignId,
    setEntityInfo,
    setAppConfig,
    setError,
    setModalInfo,
  };
}
